import React from "react";

interface HeatmapStatsCardProps {
  monthlyAverage: number;
  monthlyTrend: number;
  peakMonths: { month: string; count: number }[];
  lowestMonths: { month: string; count: number }[];
}

const HeatmapStatsCard: React.FC<HeatmapStatsCardProps> = ({
  monthlyAverage,
  monthlyTrend,
  peakMonths,
  lowestMonths
}) => {

  // Function to format numbers
  const formatNumber = (num: number): string => {
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
    if (num >= 100_000) return Math.round(num / 1_000) + "K";
    if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
    return num.toString();
  };
  // Color palette based on #f8841c (orange)
  const colors = {
    primary: '#f8841c',       // Main orange
    primaryDark: '#d87316',   // Darker orange
    primaryLight: '#fde7d9',  // Light orange tint
    secondary: '#3B3F6E',     // Complementary dark blue
    background: '#FFFFFF',    // White background
    cardBg: '#F8F9FC',        // Light gray for cards
    positive: '#4A8B6E',      // Green for positive trends
    negative: '#A44C4C',      // Red for negative trends
    textDark: '#2D3748',      // Dark text
    textMedium: '#4A5568',    // Medium text
    textLight: '#718096'      // Light text
  };

  // Extract month names and counts from the props
  const mostAffectedMonths = peakMonths.map(item => item.month);
  const mostAffectedCounts = peakMonths.map(item => item.count);
  const leastAffectedMonths = lowestMonths.map(item => item.month);
  const leastAffectedCounts = lowestMonths.map(item => item.count);

  return (
    <div style={{
      backgroundColor: colors.background,
      borderRadius: '14px',
      padding: '20px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      fontFamily: "'Inter', 'Helvetica', sans-serif",
      width: '100%',
      maxWidth: '420px',
      border: `1px solid ${colors.primaryLight}`,
      height: '500px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header with orange accent */}
      <div style={{ 
        marginBottom: '18px',
        borderBottom: `2px solid ${colors.primaryLight}`,
        paddingBottom: '12px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '4px'
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            backgroundColor: colors.primary,
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
          </div>
          <h4 style={{
            color: colors.textDark,
            margin: 0,
            fontSize: '18px',
            fontWeight: 700,
            letterSpacing: '-0.25px'
          }}>
            Eviction Patterns
          </h4>
        </div>
      </div>
      
      {/* Key Metrics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '14px',
        marginBottom: '18px'
      }}>
        <div style={{
          backgroundColor: colors.cardBg,
          borderRadius: '10px',
          padding: '14px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          borderLeft: `4px solid ${colors.secondary}`
        }}>
          <div style={{
            color: colors.textMedium,
            fontSize: '12px',
            fontWeight: 600,
            marginBottom: '6px',
            letterSpacing: '0.5px'
          }}>MONTHLY AVG</div>
          <div style={{
            color: colors.textDark,
            fontSize: '20px',
            fontWeight: 800,
            letterSpacing: '-0.5px'
          }}>
            {formatNumber(monthlyAverage)}
          </div>
        </div>
        
        <div style={{
          backgroundColor: colors.cardBg,
          borderRadius: '10px',
          padding: '14px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          borderLeft: `4px solid ${monthlyTrend >= 0 ? colors.positive : colors.negative}`
        }}>
          <div style={{
            color: colors.textMedium,
            fontSize: '12px',
            fontWeight: 600,
            marginBottom: '6px',
            letterSpacing: '0.5px'
          }}>MONTHLY TREND</div>
          <div style={{
            color: monthlyTrend >= 0 ? colors.positive : colors.negative,
            fontSize: '20px',
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            letterSpacing: '-0.5px'
          }}>
            {monthlyTrend >= 0 ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.positive}>
                <path d="M18 15l-6-6-6 6" strokeWidth="2"/>
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.negative}>
                <path d="M6 9l6 6 6-6" strokeWidth="2"/>
              </svg>
            )}
            {Math.abs(monthlyTrend).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Month Sections */}
      <div style={{ 
        marginBottom: '16px', 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}>
        {/* Most Affected Months */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px'
          }}>
            <div style={{
              color: colors.textDark,
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.5px'
            }}>
              Peak Months
            </div>
            <div style={{
              color: colors.primaryDark,
              fontSize: '11px',
              fontWeight: 600,
              backgroundColor: colors.primaryLight,
              padding: '4px 10px',
              borderRadius: '12px'
            }}>
              Highest Volume
            </div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '10px'
          }}>
            {mostAffectedMonths.map((month, index) => (
              <div key={month} style={{
                backgroundColor: colors.cardBg,
                borderRadius: '8px',
                padding: '12px 8px',
                textAlign: 'center',
                boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                borderBottom: `3px solid ${colors.primary}`
              }}>
                <div style={{
                  color: colors.textDark,
                  fontSize: '13px',
                  fontWeight: 700,
                  marginBottom: '6px'
                }}>
                  {month}
                </div>
                <div style={{
                  color: colors.textDark,
                  fontSize: '16px',
                  fontWeight: 800
                }}>
                  {formatNumber(mostAffectedCounts[index])}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Least Affected Months */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px'
          }}>
            <div style={{
              color: colors.textMedium,
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.5px'
            }}>
              Low Months
            </div>
            <div style={{
              color: colors.textMedium,
              fontSize: '11px',
              fontWeight: 600,
              backgroundColor: colors.primaryLight,
              padding: '4px 10px',
              borderRadius: '12px'
            }}>
              Lowest Volume
            </div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '10px'
          }}>
            {leastAffectedMonths.map((month, index) => (
              <div key={month} style={{
                backgroundColor: colors.cardBg,
                borderRadius: '8px',
                padding: '12px 8px',
                textAlign: 'center',
                boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                borderBottom: `3px solid ${colors.primaryLight}`
              }}>
                <div style={{
                  color: colors.textMedium,
                  fontSize: '13px',
                  fontWeight: 700,
                  marginBottom: '6px'
                }}>
                  {month}
                </div>
                <div style={{
                  color: colors.textMedium,
                  fontSize: '16px',
                  fontWeight: 800
                }}>
                  {formatNumber(leastAffectedCounts[index])}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div style={{
        paddingTop: '12px',
        borderTop: `1px solid ${colors.primaryLight}`,
        color: colors.textLight,
        fontSize: '11px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={colors.textLight} style={{ marginRight: '6px' }}>
            <circle cx="12" cy="12" r="10" strokeWidth="2"/>
            <path d="M12 8v4l3 3" strokeWidth="2"/>
          </svg>
          <span>Updated: {new Date().toLocaleDateString()}</span>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center'
        }}>
          <div style={{
            width: '6px',
            height: '6px',
            backgroundColor: colors.primary,
            borderRadius: '50%',
            marginRight: '6px'
          }}></div>
          <span>Live Data</span>
        </div>
      </div>
    </div>
  );
};

export default HeatmapStatsCard;